// iOS-style shell: thin preview strip, compact sticky nav with a back
// button and inline title, large title rendered in the content area (not
// in the sticky bar — Apple's large-title pattern). ToastHost at the
// bottom.

import type { ReactNode } from "react";
import { showToast, ToastHost } from "./toast";

type Props = {
  title: string;
  largeTitle?: boolean;
  rightAccessory?: ReactNode;
  device?: string;
  signedInAs?: string;
  connection?: "wifi" | "cellular" | "offline";
  children: ReactNode;
};

export function MockShell({
  title,
  largeTitle = true,
  rightAccessory,
  device = "Floor iPad 2",
  signedInAs = "Jake M.",
  connection = "wifi",
  children,
}: Props) {
  return (
    <div className="min-h-screen flex flex-col text-slate-900">
      <div className="bg-slate-900 text-slate-300 text-[11px] py-1 px-3 flex items-center justify-center gap-2 tracking-wide">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
        Preview — mockup data
      </div>

      <header className="bg-white/80 backdrop-blur-xl sticky top-0 z-20 hairline">
        <div className="flex items-center justify-between gap-3 px-3 sm:px-5 py-2">
          <button
            type="button"
            className="min-h-10 px-2 text-[15px] font-normal text-[#007aff] flex items-center gap-0.5"
            onClick={() => {
              window.location.hash = "#/mocks";
            }}
          >
            <span className="text-xl leading-none">‹</span>
            <span>Mocks</span>
          </button>

          {!largeTitle && (
            <h1 className="text-[17px] font-semibold tracking-tight truncate">{title}</h1>
          )}

          <div className="flex items-center gap-2">
            {rightAccessory}
            <span className="hidden md:inline text-[13px] text-slate-500">{device}</span>
            <ConnectionDot connection={connection} />
            <button
              type="button"
              onClick={() => showToast("Would open user menu")}
              className="min-h-10 flex items-center gap-2 rounded-full pl-0.5 pr-1 py-1"
            >
              <span className="h-8 w-8 rounded-full bg-[#052460] text-white grid place-items-center text-xs font-semibold">
                {signedInAs
                  .split(" ")
                  .map((p) => p[0])
                  .join("")
                  .slice(0, 2)}
              </span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {largeTitle && (
          <div className="px-4 sm:px-6 pt-4 pb-2">
            <h1 className="text-[34px] leading-[1.1] font-bold tracking-tight">{title}</h1>
          </div>
        )}
        {children}
      </main>
      <ToastHost />
    </div>
  );
}

function ConnectionDot({ connection }: { connection: "wifi" | "cellular" | "offline" }) {
  const color = connection === "offline" ? "bg-amber-400" : "bg-emerald-500";
  return <span className={`h-1.5 w-1.5 rounded-full ${color}`} aria-hidden="true" />;
}

type TouchButtonProps = {
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "danger" | "destructive";
  onClick?: () => void;
  disabled?: boolean;
  full?: boolean;
  size?: "sm" | "md" | "lg";
  mock?: string;
};

export function TouchButton({
  children,
  variant = "primary",
  onClick,
  disabled,
  full,
  size = "md",
  mock,
}: TouchButtonProps) {
  const variantClass = {
    primary: "bg-[#052460] text-white active:bg-[#0a3291]",
    secondary: "bg-slate-100 text-slate-900 active:bg-slate-200",
    ghost: "bg-transparent text-[#007aff]",
    danger: "bg-[#ff3b30] text-white active:bg-rose-700",
    destructive: "bg-transparent text-[#ff3b30]",
  }[variant];
  const sizeClass =
    size === "lg"
      ? "min-h-12 px-6 text-[17px]"
      : size === "sm"
        ? "min-h-9 px-3 text-[13px]"
        : "min-h-11 px-5 text-[15px]";
  const handle = () => {
    if (onClick) onClick();
    else if (mock) showToast(mock);
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      className={`${variantClass} ${sizeClass} ${full ? "w-full" : ""} rounded-full font-medium transition active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

export function Check({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`inline-block ${className}`}
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

export function Chevron({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`inline-block ${className}`}
      width="7"
      height="12"
      viewBox="0 0 7 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="1 1 6 6 1 11" />
    </svg>
  );
}

// Grouped inset list (iOS Settings pattern). Children are rows; separator
// handled by the parent so inner rows don't need to think about it.
export function Group({
  header,
  footer,
  children,
}: {
  header?: string;
  footer?: string;
  children: ReactNode;
}) {
  return (
    <section className="mb-6">
      {header && (
        <h2 className="text-[13px] font-normal text-slate-500 uppercase tracking-wider px-4 pb-1.5">
          {header}
        </h2>
      )}
      <div className="bg-white rounded-2xl overflow-hidden">{children}</div>
      {footer && <p className="text-[12px] text-slate-500 px-4 pt-1.5">{footer}</p>}
    </section>
  );
}

export function Row({
  leading,
  title,
  subtitle,
  value,
  trailing,
  destructive,
  onClick,
  mock,
  chevron,
}: {
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  value?: ReactNode;
  trailing?: ReactNode;
  destructive?: boolean;
  onClick?: () => void;
  mock?: string;
  chevron?: boolean;
}) {
  const interactive = Boolean(onClick || mock);
  const Component: "button" | "div" = interactive ? "button" : "div";
  const handle = () => {
    if (onClick) onClick();
    else if (mock) showToast(mock);
  };
  const titleColor = destructive ? "text-[#ff3b30]" : "text-slate-900";
  return (
    <Component
      type={interactive ? "button" : undefined}
      onClick={interactive ? handle : undefined}
      className={`w-full text-left flex items-center gap-3 px-4 py-3 min-h-12 hairline last:shadow-none ${interactive ? "active:bg-slate-50" : ""}`}
    >
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="flex-1 min-w-0">
        <span className={`block text-[15px] font-normal ${titleColor} truncate`}>{title}</span>
        {subtitle && <span className="block text-[13px] text-slate-500 truncate">{subtitle}</span>}
      </span>
      {value && <span className="text-[15px] text-slate-500 tabular-nums shrink-0">{value}</span>}
      {trailing}
      {chevron && <Chevron className="text-slate-300" />}
    </Component>
  );
}
